import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCallback, useEffect, useRef } from "react";

/**
 * Lightweight, fire-and-forget activity logger. Sends one event to /api/track,
 * silently swallows any failure (we don't want analytics to disrupt UX), and
 * is a no-op for unauthenticated visitors and admins (the backend also ignores
 * those cases — this is just an early-exit optimisation).
 *
 * Supported event types (mirror backend ALLOWED_EVENT_TYPES):
 *   VIEW_CARD · TAP_IMAGE · LONG_PRESS · RIGHT_CLICK · DEVTOOLS_OPEN
 *
 * Also wires up window-level devtools detection so a single mount in the app
 * shell catches any inspector usage during the session.
 */
export function useActivityTracker() {
  const { user } = useAuth();
  const userRef = useRef(user);
  userRef.current = user;

  const track = useCallback((event_type, payload = {}) => {
    const u = userRef.current;
    if (!u || u.role === "admin") return;
    // Use sendBeacon-style fire-and-forget; api uses axios so we just catch and
    // discard rejections. Network blip = no problem.
    api.post("/track", { event_type, ...payload }).catch(() => {});
  }, []);

  return track;
}

/**
 * Global devtools detector. Mounted once near the app root. Uses the classic
 * `console + Object getter` trick that fires only when DevTools/Inspector
 * actively reads the object (Chromium, Firefox, Edge). Reports at most once
 * per session via sessionStorage to avoid spamming alerts.
 */
export function useDevtoolsDetector(onDetected) {
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (sessionStorage.getItem("sv_devtools_reported") === "1") return undefined;

    let reported = false;
    const fire = () => {
      if (reported) return;
      reported = true;
      sessionStorage.setItem("sv_devtools_reported", "1");
      try { onDetected?.(); } catch { /* ignore */ }
    };

    // Trick 1: a probe object whose toString runs when devtools logs it
    const probe = {};
    Object.defineProperty(probe, "id", {
      get() { fire(); return ""; },
    });

    // Trick 2: window size diff (works when DevTools is docked)
    const sizeCheck = () => {
      const wDiff = window.outerWidth - window.innerWidth;
      const hDiff = window.outerHeight - window.innerHeight;
      // Allow ~160px for browser chrome; threshold above that → likely DevTools docked
      if (wDiff > 200 || hDiff > 220) fire();
    };

    const interval = setInterval(() => {
      // re-trigger probe; if DevTools is open, the getter fires
      try { console.debug(probe); console.clear?.(); } catch { /* ignore */ }
      sizeCheck();
    }, 4000);

    window.addEventListener("resize", sizeCheck, { passive: true });

    return () => {
      clearInterval(interval);
      window.removeEventListener("resize", sizeCheck);
    };
  }, [onDetected]);
}
